import { useState } from 'react'

interface SMSMockupProps {
  shopName: string
  shopLogo?: string
  message: string
  customerName?: string
  loading?: boolean
}

// Fallback avatar with initial
function AvatarWithFallback({ name, src }: { name: string; src?: string }) {
  const [imgError, setImgError] = useState(false)
  const initial = name.charAt(0).toUpperCase()

  if (!src || imgError) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#8e8e93',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '20px',
          fontWeight: 500,
        }}
      >
        {initial}
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={name}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onError={() => setImgError(true)}
    />
  )
}

// Small avatar for message bubble
function SmallAvatar({ name, src }: { name: string; src?: string }) {
  const [imgError, setImgError] = useState(false)
  const initial = name.charAt(0).toUpperCase()

  if (!src || imgError) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#8e8e93',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '10px',
          fontWeight: 500,
        }}
      >
        {initial}
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={name}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onError={() => setImgError(true)}
    />
  )
}

// Icons
function ChevronLeft() {
  return (
    <svg width="12" height="20" viewBox="0 0 12 20" fill="none">
      <path d="M10 2L2 10L10 18" stroke="#007AFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function VideoIcon() {
  return (
    <svg width="28" height="20" viewBox="0 0 28 20" fill="none">
      <rect x="1" y="3" width="18" height="14" rx="3" stroke="#007AFF" strokeWidth="2"/>
      <path d="M19 8L26 4V16L19 12V8Z" fill="#007AFF"/>
    </svg>
  )
}

function ChevronRight() {
  return (
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
      <path d="M1.5 1L6.5 6L1.5 11" stroke="#8e8e93" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

export default function SMSMockup({ shopName, shopLogo, message, customerName = 'Sarah', loading = false }: SMSMockupProps) {
  // Replace {customer_name} placeholder with actual name
  const displayMessage = message.replace(/\{customer_name\}/gi, customerName)

  return (
    <>
      {/* Typing animation keyframes */}
      <style>{`
        @keyframes typing {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    <div style={styles.phoneFrame}>
      {/* Dynamic Island */}
      <div style={styles.dynamicIsland} />

      {/* Side Buttons */}
      <div style={{ ...styles.sideButton, top: '96px', left: '-3px', height: '28px', borderRadius: '2px 0 0 2px' }} />
      <div style={{ ...styles.sideButton, top: '144px', left: '-3px', height: '48px', borderRadius: '2px 0 0 2px' }} />
      <div style={{ ...styles.sideButton, top: '208px', left: '-3px', height: '48px', borderRadius: '2px 0 0 2px' }} />
      <div style={{ ...styles.sideButton, top: '176px', right: '-3px', height: '80px', borderRadius: '0 2px 2px 0' }} />

      {/* Screen */}
      <div style={styles.screen}>
        {/* Status Bar */}
        <div style={styles.statusBar}>
          <div style={styles.time}>9:41</div>
          <div style={styles.statusIcons}>
            <svg width="18" height="12" viewBox="0 0 18 12" fill="none">
              <rect x="0" y="4" width="3" height="8" rx="1" fill="#000"/>
              <rect x="5" y="2" width="3" height="10" rx="1" fill="#000"/>
              <rect x="10" y="0" width="3" height="12" rx="1" fill="#000"/>
              <rect x="15" y="0" width="3" height="12" rx="1" fill="#000" fillOpacity="0.3"/>
            </svg>
            <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
              <path d="M8 2C11.14 2 13.92 3.59 15.36 6.04C15.44 6.18 15.44 6.34 15.36 6.48C13.92 8.91 11.14 10.5 8 10.5C4.86 10.5 2.08 8.91 0.64 6.48C0.56 6.34 0.56 6.18 0.64 6.04C2.08 3.59 4.86 2 8 2Z" stroke="#000" strokeWidth="1.5"/>
            </svg>
            <svg width="27" height="13" viewBox="0 0 27 13" fill="none">
              <rect x="0.5" y="0.5" width="23" height="12" rx="3.5" stroke="#000" strokeOpacity="0.35"/>
              <rect x="2" y="2" width="18" height="9" rx="2" fill="#000"/>
              <path d="M25 4V9C25.83 8.65 26.5 7.87 26.5 6.5C26.5 5.13 25.83 4.35 25 4Z" fill="#000" fillOpacity="0.4"/>
            </svg>
          </div>
        </div>

        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backButton}>
            <ChevronLeft />
          </button>

          <div style={styles.profileCenter}>
            <div style={styles.avatar}>
              <AvatarWithFallback name={shopName} src={shopLogo} />
            </div>
            <div style={styles.nameRow}>
              <span style={styles.name}>{shopName}</span>
              <ChevronRight />
            </div>
          </div>

          <button style={styles.videoButton}>
            <VideoIcon />
          </button>
        </div>

        {/* Messages Area */}
        <div style={styles.messagesArea}>
          {/* Timestamp */}
          <div style={styles.timestamp}>
            <span style={{ fontWeight: 500 }}>iMessage</span>
            <br />
            <span>Today 9:41 AM</span>
          </div>

          {loading ? (
            /* Typing indicator */
            <div style={styles.messageRow}>
              <div style={styles.smallAvatar}>
                <SmallAvatar name={shopName} src={shopLogo} />
              </div>
              <div style={{ ...styles.messageBubbleGray, padding: '14px 18px' }}>
                <div style={styles.typingDots}>
                  <div style={{ ...styles.dot, animationDelay: '0ms' }} />
                  <div style={{ ...styles.dot, animationDelay: '150ms' }} />
                  <div style={{ ...styles.dot, animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          ) : (
            /* Shop message (incoming - gray) */
            <div style={styles.messageRow}>
              <div style={styles.smallAvatar}>
                <SmallAvatar name={shopName} src={shopLogo} />
              </div>
              <div style={styles.messageBubbleGray}>
                {displayMessage}
              </div>
            </div>
          )}
        </div>

        {/* Input Area (just visual, not functional) */}
        <div style={styles.inputArea}>
          <div style={styles.inputRow}>
            <button style={styles.plusButton}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#8e8e93" strokeWidth="1.5"/>
                <path d="M12 7V17M7 12H17" stroke="#8e8e93" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            <div style={styles.textInput}>
              <span style={{ color: '#8e8e93' }}>iMessage</span>
            </div>
            <button style={styles.sendButton}>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="16" fill="#007AFF"/>
                <path d="M10 16L16 10L22 16M16 10V22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" transform="rotate(-90 16 16)"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

const styles: Record<string, React.CSSProperties> = {
  phoneFrame: {
    position: 'relative',
    width: '280px',
    height: '580px',
    borderRadius: '50px',
    backgroundColor: '#000',
    boxShadow: '0 0 0 3px #3f3f46, 0 0 0 7px #27272a, 0 25px 50px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  dynamicIsland: {
    position: 'absolute',
    top: '12px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100px',
    height: '28px',
    backgroundColor: '#000',
    borderRadius: '20px',
    zIndex: 100,
  },
  sideButton: {
    position: 'absolute',
    width: '3px',
    backgroundColor: '#52525b',
  },
  screen: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    right: '4px',
    bottom: '4px',
    backgroundColor: '#fff',
    borderRadius: '46px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  statusBar: {
    height: '44px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 24px',
    paddingTop: '8px',
  },
  time: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#000',
  },
  statusIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  header: {
    height: '72px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px',
    paddingBottom: '8px',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    backgroundColor: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    position: 'relative',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    zIndex: 10,
  },
  videoButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    zIndex: 10,
  },
  profileCenter: {
    position: 'absolute',
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: '#e5e5e5',
    marginBottom: '2px',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },
  name: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#000',
  },
  messagesArea: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
  timestamp: {
    textAlign: 'center',
    fontSize: '11px',
    color: '#8e8e93',
    marginBottom: '16px',
    lineHeight: 1.4,
  },
  messageRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    marginBottom: '4px',
  },
  smallAvatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
  },
  messageBubbleGray: {
    maxWidth: '75%',
    padding: '6px 10px',
    backgroundColor: '#e9e9eb',
    borderRadius: '16px',
    borderBottomLeftRadius: '4px',
    fontSize: '11px',
    lineHeight: 1.4,
    color: '#000',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  messageBubbleBlue: {
    maxWidth: '75%',
    padding: '6px 10px',
    backgroundColor: '#007AFF',
    borderRadius: '16px',
    borderBottomRightRadius: '4px',
    fontSize: '11px',
    lineHeight: 1.4,
    color: '#fff',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    marginLeft: 'auto',
  },
  inputArea: {
    padding: '8px 12px',
    paddingBottom: '24px',
    borderTop: '1px solid rgba(0,0,0,0.1)',
    backgroundColor: '#fff',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  plusButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textInput: {
    flex: 1,
    height: '36px',
    borderRadius: '18px',
    border: '1px solid #e5e5e5',
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '15px',
  },
  sendButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  typingDots: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  dot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#8e8e93',
    borderRadius: '50%',
    animation: 'typing 1s infinite ease-in-out',
  },
}
